import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { FaBan, FaBuilding, FaClipboardList, FaGem } from 'react-icons/fa';

const Movement = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    {
      Icon: FaBan,
      title: "No VC",
      subtitle: "They dumped on you last time",
      description: "Every 'strategic investor' that touched the original $RIZZMAS vanished when the music stopped. This time? Every single dollar comes from real believers putting up their own money. No cap tables. No vesting schedules. No private sales to dump on retail.",
      color: "text-rizzmas-red",
      bgColor: "from-rizzmas-red/20 to-rizzmas-red/5",
      borderColor: "border-rizzmas-red/30",
      glowColor: "shadow-rizzmas-red/20",
      delay: 0
    },
    {
      Icon: FaBuilding,
      title: "No Company",
      subtitle: "Because they all abandoned ship",
      description: "The original devs? Gone. The marketing team? Vanished. The 'CEO'? Probably on a beach somewhere. RizzmasCTO exists because 11 influencers got tired of waiting for someone else to bring back the magic. No LLC. No corporate structure. Just vibes and capital.",
      color: "text-rizzmas-gold",
      bgColor: "from-rizzmas-gold/20 to-rizzmas-gold/5",
      borderColor: "border-rizzmas-gold/30",
      glowColor: "shadow-rizzmas-gold/20",
      delay: 0.15
    },
    {
      Icon: FaClipboardList,
      title: "No Corporate Roadmap",
      subtitle: "The community decides everything",
      description: "Forget quarterly earnings calls and pivot announcements. The roadmap is simple: make Rizzmas unforgettable again. How? The community decides. Every marketing push. Every meme. Every exchange listing. It's all transparent, tracked, and driven by what the holders want.",
      color: "text-rizzmas-green",
      bgColor: "from-rizzmas-green/20 to-rizzmas-green/5",
      borderColor: "border-rizzmas-green/30",
      glowColor: "shadow-rizzmas-green/20",
      delay: 0.3
    },
  ];

  const titleVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 30
    },
    visible: (index) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        delay: 0.5 + index * 0.1,
        ease: "easeOut"
      }
    })
  };

  return (
    <section ref={ref} className="relative py-40 px-4 overflow-hidden">
      {/* Subtle background */}
      <div className="absolute inset-0 bg-gradient-to-b from-transparent via-rizzmas-green/3 to-transparent" />

      <div className="max-w-6xl mx-auto relative z-10">
        {/* Animated Title */}
        <motion.div
          variants={titleVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="text-center mb-16"
        >
          <motion.h2
            className="text-5xl md:text-7xl font-black mb-4 bg-gradient-to-r from-rizzmas-green to-rizzmas-gold bg-clip-text text-transparent"
            animate={isInView ? {
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            } : {}}
            transition={{
              duration: 5,
              repeat: Infinity,
              ease: "linear"
            }}
            style={{
              backgroundSize: '200% 200%',
            }}
          >
            The Movement
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="text-xl md:text-2xl text-rizzmas-cream/80 max-w-3xl mx-auto"
          >
            What happens when the suits abandon the project but the community refuses to let it die?
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ delay: 0.9, duration: 0.6 }}
            className="text-lg text-rizzmas-gold/90 font-semibold mt-3"
          >
            You get the most authentic comeback story in crypto.
          </motion.p>
        </motion.div>

        {/* Feature Cards */}
        <div className="grid md:grid-cols-3 gap-8 mb-20">
          {features.map((feature, index) => {
            const IconComponent = feature.Icon;
            return (
              <motion.div
                key={index}
                custom={index}
                variants={cardVariants}
                initial="hidden"
                animate={isInView ? "visible" : "hidden"}
                className={`bg-gradient-to-br ${feature.bgColor} backdrop-blur-sm border ${feature.borderColor} rounded-2xl p-8 text-center`}
              >
                <div className={`text-6xl mb-6 flex justify-center ${feature.color}`}>
                  <IconComponent />
                </div>

                <h3 className="text-2xl font-bold text-rizzmas-cream mb-2">
                  {feature.title}
                </h3>

                <p className="text-sm italic text-rizzmas-cream/50 mb-4">
                  {feature.subtitle}
                </p>

                <p className="text-rizzmas-cream/80 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* Main Message Card */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{
            duration: 0.6,
            delay: 0.8,
            ease: "easeOut"
          }}
          className="relative bg-gradient-to-br from-rizzmas-red/20 via-rizzmas-dark to-rizzmas-green/20 border-2 border-rizzmas-gold/50 rounded-3xl p-12 text-center"
        >
          <h3 className="text-3xl md:text-5xl font-black text-white mb-4 leading-tight">
            This Isn't Corporate Crypto.
          </h3>

          <p className="text-xl md:text-2xl text-rizzmas-cream/90 mb-6 leading-relaxed">
            This is what happens when 11 influencers get in a group chat and decide they're done waiting for someone else to bring back the magic.
          </p>

          <p className="text-lg md:text-xl text-rizzmas-gold font-bold mb-4">
            $10,000 each. No expectations. No promises. Just pure belief.
          </p>
          <p className="text-base md:text-lg text-rizzmas-cream/70 mb-8 max-w-3xl mx-auto">
            They're not here to flip. They're here to BUILD. Every dollar goes straight into marketing, creator pushes, and getting RizzmasCTO on every exchange that matters. The entire war chest is tracked transparently on-chain. No dev wallets. No "team allocations." Just a community takeover funded by people who actually give a damn.
          </p>

          {/* Rally Cry and Takeover Cards */}
          <div className="flex flex-col md:flex-row gap-6 justify-center items-center mt-12">
            <div className="bg-rizzmas-red/30 border-2 border-rizzmas-red rounded-xl p-6 flex-1">
              <p className="text-xl font-bold text-rizzmas-cream mb-2">
                Every donation is a <span className="text-rizzmas-gold">rally cry</span>
              </p>
              <p className="text-sm text-rizzmas-cream/70">
                When you contribute, you're not just funding marketing. You're signaling to everyone watching that the comeback is real. That Rizzmas isn't dead. That the community is back.
              </p>
            </div>

            {/* Gem in Center */}
            <div className="text-4xl text-rizzmas-gold">
              <FaGem />
            </div>

            <div className="bg-rizzmas-green/30 border-2 border-rizzmas-green rounded-xl p-6 flex-1">
              <p className="text-xl font-bold text-rizzmas-cream mb-2">
                Every dollar fuels the <span className="text-rizzmas-gold">takeover</span>
              </p>
              <p className="text-sm text-rizzmas-cream/70">
                100% of donations go directly to making noise. TikTok trends. Twitter spaces. Exchange listings. Meme competitions. Whatever it takes to make sure Rizzmas dominates this holiday season.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Movement;
